import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';
import AuthService from '../../services/authService';

const ShowingManagementTab = ({ onShowingChanged }) => {
  const [showings, setShowings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [selectedShowingId, setSelectedShowingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resendingId, setResendingId] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(null);

  // Fetch showings and agents on component mount
  useEffect(() => {
    fetchShowings();
    fetchAgents();
  }, []);

  // Apply filter when status filter changes
  useEffect(() => {
    fetchShowings();
  }, [statusFilter]);

  // Function to fetch showings with optional status filter
  const fetchShowings = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      
      let url = 'https://back-end-x7tx.onrender.com/api/showings';
      
      // Add query parameters for filters if needed
      if (Object.keys(filters).length > 0) {
        const queryParams = new URLSearchParams();
        for (const key in filters) {
          queryParams.append(key, filters[key]);
        }
        url += `?${queryParams.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      const showingsData = data.data || [];
      
      // Sort showings by date, most recent first
      const sortedShowings = showingsData.sort((a, b) => {
        const dateA = new Date(a.date || a.createdAt);
        const dateB = new Date(b.date || b.createdAt);
        return dateB - dateA; // Descending order (newest first)
      });
      
      setShowings(sortedShowings);
    } catch (err) {
      console.error('Error fetching showings:', err);
      setError(`Failed to fetch showings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch available agents
  const fetchAgents = async () => {
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/admin/agents', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch agents');
      }
      
      const data = await response.json();
      setAgents(data.data || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Handle agent assignment
  const handleAssignAgent = async (showingId) => {
    if (!selectedAgentId) {
      alert('Please select an agent to assign');
      return;
    }

    try {
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/showings/${showingId}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ agentId: selectedAgentId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign agent');
      }

      // Refresh showings list
      fetchShowings();
      if (onShowingChanged) onShowingChanged();
      
      // Reset selection
      setSelectedAgentId('');
      setSelectedShowingId(null);
      
      alert('Agent assigned successfully!');
    } catch (err) {
      alert(`Failed to assign agent: ${err.message}`);
      console.error('Error assigning agent:', err);
    }
  };

  // Resend pool webhook for unclaimed user showings
  const handleResendPool = async (showingId) => {
    setResendingId(showingId);
    setResendSuccess(null);
    try {
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/showings/${showingId}/resend-pool`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to resend');
      setResendSuccess(showingId);
      setTimeout(() => setResendSuccess(null), 3000);
    } catch (err) {
      alert(`Failed to resend: ${err.message}`);
    } finally {
      setResendingId(null);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ color: '#ffffff', textAlign: 'center', padding: '3rem' }}>
        <div style={{
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'rgba(255, 255, 255, 0.8)',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }}></div>
        <p>Loading showings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'rgba(220, 38, 38, 0.15)',
        color: '#fca5a5',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        padding: '12px 16px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ color: '#ffffff' }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .filter-header {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
          border-radius: 12px;
          padding: 24px !important;
          margin-bottom: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .filter-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .filter-label {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
          font-weight: 500;
        }
        .form-control {
          background-color: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
          padding: 8px 12px;
          border-radius: 6px;
          min-width: 200px;
        }
        .form-control:focus {
          border-color: rgba(255, 255, 255, 0.4) !important;
          background-color: rgba(255, 255, 255, 0.08) !important;
          outline: none;
        }
        .form-control option {
          background-color: #1a1a1a;
          color: #ffffff;
        }
        .table-responsive {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          overflow-x: auto;
        }
        .table {
          color: #ffffff !important;
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background-color: transparent !important;
        }
        .table * {
          background-color: transparent !important;
        }
        .table thead {
          background-color: transparent !important;
        }
        .table th {
          color: rgba(255, 255, 255, 0.7) !important;
          border-bottom: 2px solid rgba(255, 255, 255, 0.1) !important;
          background-color: transparent !important;
          padding: 16px !important;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          text-align: left;
        }
        .table td {
          padding: 16px !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          color: #ffffff !important;
          background-color: transparent !important;
        }
        .table tr {
          transition: background-color 0.2s;
          background-color: transparent !important;
        }
        .table tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.03) !important;
        }
        .table tbody tr:hover * {
          background-color: transparent !important;
        }
        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }
        .status-scheduled {
          background-color: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
        }
        .status-completed {
          background-color: rgba(34, 197, 94, 0.2);
          color: #4ade80;
        }
        .status-cancelled {
          background-color: rgba(239, 68, 68, 0.2);
          color: #f87171;
        }
        .status-unknown {
          background-color: rgba(148, 163, 184, 0.2);
          color: #94a3b8;
        }
        .agent-assignment {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 200px;
        }
        .btn-group {
          display: flex;
          gap: 8px;
        }
        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.875rem;
          border: none;
        }
        .btn-primary {
          background-color: #4f46e5;
          color: #ffffff;
        }
        .btn-primary:hover:not(:disabled) {
          background-color: #4338ca;
        }
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-secondary {
          background-color: transparent;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        .btn-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.4);
        }
        .btn-outline-primary {
          background-color: transparent;
          color: #60a5fa;
          border: 1px solid #60a5fa;
        }
        .btn-outline-primary:hover {
          background-color: rgba(96, 165, 250, 0.1);
        }
        .btn-sm {
          padding: 6px 12px;
          font-size: 0.8125rem;
        }
        .mb-2 {
          margin-bottom: 0.5rem;
        }
        .no-data {
          text-align: center;
          padding: 3rem;
          color: rgba(255, 255, 255, 0.6);
        }
      `}</style>

      <div className="filter-header">
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Manage Showings</h2>
        <div className="filter-group">
          <label htmlFor="statusFilter" className="filter-label">Filter by Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="form-control"
          >
            <option value="all">All Showings</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {showings.length === 0 ? (
        <div className="table-responsive">
          <div className="no-data">
            <p>No showings found.</p>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Property</th>
                <th>User</th>
                <th>Date</th>
                <th>Time Slot</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Assigned Agent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {showings.map((showing) => (
                <tr key={showing._id}>
                  <td>
                    {showing.property && showing.property.address ? 
                      showing.property.address : 'Address not available'}
                  </td>
                  <td>{showing.user?.name || showing.user?.email || 'N/A'}</td>
                  <td>{showing.date ? formatDate(showing.date) : 'N/A'}</td>
                  <td>{showing.timeSlot || 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${showing.status || 'unknown'}`}>
                      {showing.status || 'Unknown'}
                    </span>
                  </td>
                  <td>{showing.notes || 'N/A'}</td>
                  <td>{showing.agent ? showing.agent.name : 'Not Assigned'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* Assign Agent (scheduled only) */}
                      {showing.status === 'scheduled' && (
                        selectedShowingId === showing._id ? (
                          <div className="agent-assignment">
                            <select
                              className="form-control mb-2"
                              value={selectedAgentId}
                              onChange={(e) => setSelectedAgentId(e.target.value)}
                            >
                              <option value="">Select Agent</option>
                              {agents.map((agent) => (
                                <option key={agent._id} value={agent._id}>
                                  {agent.name}
                                </option>
                              ))}
                            </select>
                            <div className="btn-group">
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleAssignAgent(showing._id)}
                                disabled={!selectedAgentId}
                              >
                                Assign
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setSelectedShowingId(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setSelectedShowingId(showing._id)}
                          >
                            {showing.agent ? 'Reassign' : 'Assign Agent'}
                          </button>
                        )
                      )}
                      {/* Resend Pool Webhook (all statuses) */}
                      <button
                        className="btn btn-sm"
                        style={{
                          backgroundColor: resendSuccess === showing._id
                            ? 'rgba(34, 197, 94, 0.2)'
                            : 'rgba(253, 215, 0, 0.12)',
                          color: resendSuccess === showing._id ? '#4ade80' : '#fdd700',
                          border: `1px solid ${resendSuccess === showing._id ? 'rgba(34,197,94,0.4)' : 'rgba(253,215,0,0.3)'}`,
                          transition: 'all 0.2s'
                        }}
                        onClick={() => handleResendPool(showing._id)}
                        disabled={resendingId === showing._id}
                      >
                        {resendingId === showing._id
                          ? 'Sending...'
                          : resendSuccess === showing._id
                          ? '✓ Sent!'
                          : '📨 Resend to Pool'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ShowingManagementTab;